import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n/LanguageContext';
import { loadEvents } from '../../lib/events';
import './EventsDisplay.css';

const MONTHS = {
  es: ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'],
  en: ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'],
};

function toYMD(str) {
  const s = String(str || '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const [m, d, y] = s.split('/').map(Number);
  if (!y || !m || !d) return '';
  return `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
}

function parseEvent(f, lang) {
  const p = f.properties || {};
  const ymd = toYMD(p.date);
  const [y, m, d] = ymd.split('-');
  const months = MONTHS[lang] || MONTHS.es;
  return {
    ...p,
    ymd,
    day: parseInt(d, 10),
    month: months[parseInt(m, 10) - 1],
    year: y,
  };
}

export default function EventsDisplay() {
  const { t, lang } = useT();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    loadEvents()
      .then(data => {
        if (!mounted) return;
        if (!data) { setError(t('events.error')); return; }
        const today = new Date().toISOString().slice(0, 10);
        const items = (data.features || [])
          .map(f => parseEvent(f, lang))
          .filter(e => e.ymd && e.ymd >= today)
          .sort((a, b) => a.ymd.localeCompare(b.ymd));
        setEvents(items);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [lang, t]);

  useEffect(() => {
    const nodes = listRef.current?.querySelectorAll('[data-reveal]');
    if (!nodes?.length) return;
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          obs.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
    nodes.forEach(n => io.observe(n));
    return () => io.disconnect();
  }, [events]);

  return (
    <section className="evd" id="evd-section">
      <div className="evd__bg" aria-hidden="true" />
      <div className="evd__inner">
        <header className="evd__head">
          <span className="ck-eyebrow">{t('events.eyebrow')}</span>
          <h2 className="ck-section-title">
            {t('events.titleA')} <em>{t('events.titleB')}</em>
          </h2>
          <p className="evd__lede">{t('events.lede')}</p>
        </header>

        {loading && <p className="evd__status">{t('events.loading')}</p>}
        {error && !loading && <p className="evd__status">{error}</p>}

        {!loading && !error && events.length === 0 && (
          <p className="evd__status">{t('events.soon')}</p>
        )}

        {!loading && !error && events.length > 0 && (
          <ul className="evd__list" ref={listRef}>
            {events.slice(0, 6).map((e, i) => (
              <li
                key={`${e.title}-${e.ymd}-${i}`}
                className="evd__row"
                data-reveal
                style={{ '--i': i }}
              >
                <div className="evd__num">{String(i + 1).padStart(2, '0')}</div>

                <div className="evd__date">
                  <span className="evd__day">{e.day}</span>
                  <div className="evd__date-meta">
                    <span className="evd__month">{e.month}</span>
                    <span className="evd__year">{e.year}</span>
                  </div>
                </div>

                <div className="evd__info">
                  <h3 className="evd__venue">{e.title}</h3>
                  <p className="evd__city">
                    <i className="fa-solid fa-location-dot" />
                    {e.city}{e.country ? ` · ${e.country}` : ''}
                  </p>
                  {e.address && <p className="evd__addr">{e.address}</p>}
                </div>

                {e.image_url && (
                  <div className="evd__flyer-wrap">
                    <img src={e.image_url} alt={`Flyer ${e.title}`} className="evd__flyer" loading="lazy" />
                  </div>
                )}

                <div className="evd__cta">
                  {e.ticket_url ? (
                    <a
                      href={e.ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="evd__tickets"
                    >
                      <span>{t('events.tickets')}</span>
                      <i className="fa-solid fa-arrow-right" />
                    </a>
                  ) : (
                    <span className="evd__soon">{t('events.soonBtn')}</span>
                  )}
                  {e.vipticket_url && (
                    <a
                      href={e.vipticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="evd__vip"
                    >
                      {t('events.vip')}
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="evd__foot">
          <Link to="/eventos" className="ck-btn">
            {t('events.viewAll')}
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
